// Form der Antwort von GET /api/portfolio. Von Hand gepflegt und damit eine
// mögliche Fehlerquelle: Ändert sich der Server, muss das hier mitwandern.
// Ein Schemagenerator wäre die Alternative — bei einer Handvoll Typen mehr
// Werkzeugkette als Nutzen.

export type EinheitStatus = "sanierung" | "vermietbar" | "vermietet";
export type PostenStatus = "offen" | "laufend" | "fertig";
export type Prio = "hoch" | "mittel" | "niedrig";
export type Bestand = "ja" | "teilweise" | "nein" | "unklar";
export type Zustand = "gut" | "mittel" | "schlecht" | "";
export type Pflicht = "notwendig" | "optional";

export interface Anhang {
  id: string;
  dateiname: string;
  mime: string;
  groesse: number;
  rolle: string;
}

export interface Mietvertrag {
  id: string;
  einheit_id: string;
  mieter_name: string;
  kontakt: string;
  kaltmiete: string;
  nebenkosten_vz: string;
  kaution: string;
  personen: number | null;
  beginn: string | null;
  ende: string | null;
  notiz: string;
}

export interface InventarPosition {
  id: string;
  einheit_id: string;
  raum: string;
  bezeichnung: string;
  pflicht: Pflicht;
  vorhanden: Bestand;
  zustand: Zustand;
  anmerkung: string;
  neu: string;
  kosten: number;
  sortierung: number;
  dateien: Anhang[];
}

export interface Einheit {
  id: string;
  objekt_id: string;
  bezeichnung: string;
  typ: string;
  flaeche: number | null;
  zimmer: number | null;
  status: EinheitStatus;
  moebliert: boolean;
  zielmiete: number;
  notiz: string;
  mietvertrag: Mietvertrag | null;
  fruehereVertraege: Mietvertrag[];
  inventar: InventarPosition[];
  dateien: Anhang[];
}

export interface Sanierungsposten {
  id: string;
  objekt_id: string;
  gewerk: string;
  budget: number;
  ist: number;
  status: PostenStatus;
  faellig: string | null;
  notiz: string;
  dateien: Anhang[];
}

export interface Mangel {
  id: string;
  objekt_id: string;
  einheit_id: string | null;
  raum: string;
  art: string;
  beschreibung: string;
  prio: Prio;
  kosten: number;
  investiv: boolean;
  kostentraeger: string;
  beheber: string;
  faellig: string | null;
  erledigt: boolean;
  erledigt_am: string | null;
  dateien: Anhang[];
}

export interface Kennzahlen {
  invest: number;
  kaufpreis: number;
  nebenkosten: number;
  sanierungBudget: number;
  sanierungIst: number;
  maengelInvestiv: number;
  jahreskaltmieteIst: number;
  jahreskaltmietePotenzial: number;
  maengelOffen: number;
  maengelOffenKosten: number;
  inventarNeubeschaffung: number;
  inventarKritisch: number;
  renditeIst: number | null;
  renditePotenzial: number | null;
}

export interface Objekt {
  id: string;
  name: string;
  strasse: string;
  plz: string;
  ort: string;
  kaufdatum: string | null;
  kaufpreis: number;
  nebenkosten: number;
  notiz: string;
  einheiten: Einheit[];
  sanierung: Sanierungsposten[];
  maengel: Mangel[];
  dateien: Anhang[];
  kennzahlen: Kennzahlen;
}

export interface VorlagePosition {
  id: string;
  raum: string;
  bezeichnung: string;
  pflicht: Pflicht;
  sortierung: number;
}

export const WIEDERHOLUNGEN: Record<string, string> = {
  keine: "einmalig",
  monatlich: "monatlich",
  quartalsweise: "vierteljährlich",
  halbjaehrlich: "halbjährlich",
  jaehrlich: "jährlich",
};

export interface Aufgabe {
  id: string;
  titel: string;
  beschreibung: string;
  prio: Prio;
  faellig: string | null;
  zustaendig: string;
  wiederholung: string;
  erledigt: boolean;
  erledigt_am: string | null;
  objekt_id: string | null;
  einheit_id: string | null;
  dateien: Anhang[];
}

export const DOKUMENT_ARTEN = ["Kaufvertrag", "Mietvertrag", "Rechnung", "Angebot",
  "Versicherung", "Grundbuch", "Energieausweis", "Protokoll", "Behörde", "Sonstiges"];

export interface Dokument {
  id: string;
  datei_id: string;
  titel: string;
  art: string;
  datum: string | null;
  gueltig_bis: string | null;
  notiz: string;
  objekt_id: string | null;
  einheit_id: string | null;
  mietvertrag_id: string | null;
  dateiname: string;
  mime: string;
  groesse: number;
}

export const KANAELE = ["Telefon", "E-Mail", "WhatsApp", "Brief", "Persönlich", "Sonstiges"];

export interface Nachricht {
  id: string;
  richtung: "eingehend" | "ausgehend";
  kanal: string;
  partner: string;
  betreff: string;
  inhalt: string;
  zeitpunkt: string;
  objekt_id: string | null;
  einheit_id: string | null;
  mangel_id: string | null;
  dateien: Anhang[];
}

// ------------------------------------------------------------------ Finanzen

export type Richtung = "einnahme" | "ausgabe";
export type Buchungstyp = "laufend" | "investiv" | "finanzierung";

export const KONTOARTEN: Record<string, string> = {
  giro: "Girokonto", tagesgeld: "Tagesgeld", bar: "Bar",
  kreditkarte: "Kreditkarte", darlehen: "Darlehenskonto", sonstiges: "Sonstiges",
};
export const TYP_TEXT: Record<Buchungstyp, string> = {
  laufend: "laufend", investiv: "investiv", finanzierung: "Finanzierung",
};

export interface Konto {
  id: string;
  name: string;
  art: string;
  bank: string;
  startsaldo: number;
  bewegung: number;
  stand: number;
  aktiv: boolean;
}

export interface Kategorie {
  id: string;
  name: string;
  art: Richtung;
  typ: Buchungstyp;
  absetzbar: boolean;
  umlagefaehig: boolean;
  system: boolean;
  sortierung: number;
}

export interface Buchung {
  id: string;
  datum: string;
  betrag: number;
  art: Richtung;
  kategorie_id: string | null;
  konto_id: string | null;
  beschreibung: string;
  typ: Buchungstyp;
  absetzbar: boolean;
  umlagefaehig: boolean;
  objekt_id: string | null;
  einheit_id: string | null;
  mietvertrag_id: string | null;
  darlehen_id: string | null;
  kategorie_name: string | null;
  konto_name: string | null;
  dateien: Anhang[];
}

export interface Ratenspalt {
  zins: number;
  tilgung: number;
  rate: number;
  neueRestschuld: number;
  letzteRate: boolean;
}

export interface Darlehen {
  id: string;
  objekt_id: string | null;
  bank: string;
  nummer: string;
  betrag: number;
  zinssatz: number;
  rate_monatlich: number;
  beginn: string | null;
  zinsbindung_bis: string | null;
  restschuld: number;
  restschuld_am: string | null;
  aktiv: boolean;
  notiz: string;
  naechsteRate: Ratenspalt;
  zinsProJahr: number;
}

export interface Jahreszahlen {
  jahr: number;
  einnahmenIst: number;
  ausgabenIst: number;
  ausgabenLaufendIst: number;
  ausgabenInvestivIst: number;
  zinsenIst: number;
  tilgungIst: number;
  einnahmenPrognose: number;
  ausgabenPrognose: number;
  ueberschussIst: number;
  ueberschussPrognose: number;
  cashflowIst: number;
  cashflowPrognose: number;
  absetzbarIst: number;
  verbleibendeMonate: number;
}

export interface KategorieSumme {
  id: string;
  name: string;
  art: Richtung;
  typ: Buchungstyp;
  umlagefaehig: boolean;
  summe: number;
  anzahl: number;
}

export interface Uebersicht {
  aufgabenOffen: number;
  aufgabenUeberfaellig: number;
  aufgabenDemnaechst: number;
  dokumenteGesamt: number;
  dokumenteAbgelaufen: number;
  dokumenteLaufenAus: number;
  nachrichtenGesamt: number;
}

export interface Portfolio {
  objekte: Objekt[];
  vorlage: VorlagePosition[];
  kennzahlen: Kennzahlen;
  aufgaben: Aufgabe[];
  dokumente: Dokument[];
  nachrichten: Nachricht[];
  buchungen: Buchung[];
  kategorien: Kategorie[];
  konten: Konto[];
  darlehen: Darlehen[];
  finanzjahr: Jahreszahlen;
  uebersicht: Uebersicht;
  /** Nur bei manchen Aktionen dabei, etwa „Vorlage anwenden“. */
  bericht?: { ergaenzt?: number; betroffeneEinheiten?: number; uebernommen?: number };
  /** Beim Anlegen: die ID der neuen Zeile, damit direkt etwas angehängt
   *  werden kann, ohne den Datensatz erst wieder aufzurufen. */
  neueId?: string;
  /** Beim Abhaken einer wiederkehrenden Aufgabe: der nächste Termin. */
  folgeAufgabe?: { folgeId: string | null; folgeFaellig: string | null };
  /** Beim Buchen einer Darlehensrate: die Aufteilung. */
  rate?: { zins: number; tilgung: number; neueRestschuld: number; letzteRate: boolean };
}

export interface Benutzer {
  id: string;
  email: string;
  name: string;
}

/** Benutzerkonto. Heißt bewusst so und nicht "Konto": Seit den Finanzen gibt
 *  es auch Bankkonten, und die Verwechslung wäre teuer. */
export interface Benutzerkonto extends Benutzer {
  aktiv: boolean;
  erstellt_am: string;
  letzter_login: string | null;
}

/** Objekt und Mangel zusammen — für Listen über alle Objekte hinweg. */
export interface MangelMitObjekt {
  objekt: Objekt;
  mangel: Mangel;
}

export const STATUS_TEXT: Record<EinheitStatus, string> = {
  sanierung: "In Sanierung",
  vermietbar: "Vermietbar",
  vermietet: "Vermietet",
};
export const POSTEN_TEXT: Record<PostenStatus, string> = {
  offen: "Offen", laufend: "Läuft", fertig: "Fertig",
};
export const PRIO_TEXT: Record<Prio, string> = {
  hoch: "Hoch", mittel: "Mittel", niedrig: "Niedrig",
};
export const BESTAND_TEXT: Record<Bestand, string> = {
  ja: "vorhanden", teilweise: "teilweise", nein: "fehlt", unklar: "noch offen",
};
export const ZUSTAND_TEXT: Record<Zustand, string> = {
  gut: "Gut", mittel: "Mittel", schlecht: "Schlecht", "": "—",
};

export const TYPEN = ["Wohnung", "Gewerbe", "Stellplatz", "Garage", "Sonstiges"];
export const RAEUME = ["Küche", "Wohnzimmer", "Schlafzimmer", "Kinderzimmer", "Bad",
  "Gäste-WC", "Flur", "Balkon/Terrasse", "Keller", "Dachboden", "Abstellkammer",
  "Treppenhaus", "Außenanlage", "Allgemein"];
export const MANGEL_ARTEN = ["Elektro", "Sanitär", "Heizung", "Fenster/Türen", "Boden",
  "Wand/Decke", "Feuchtigkeit/Schimmel", "Möblierung/Inventar", "Außenanlage",
  "Reinigung", "Sonstiges"];
export const KOSTENTRAEGER = ["Eigentümer", "Mieter", "Versicherung",
  "Gewährleistung Handwerker", "Verkäufer", "WEG", "offen"];
export const NEU_TEXT: Record<string, string> = {
  "": "—", ja: "ja", nein: "nein", zT: "z. T.", "?": "?",
};
